import { Box, Heading, Text, HStack, Grid, VStack } from "@chakra-ui/react";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const steps = [
  {
    num: "01",
    title: "You Focus on Deals",
    body: "While you attend viewings, negotiate offers, and close transactions — let us take over the time-consuming prospecting phase of your business.",
  },
  {
    num: "02",
    title: "We Handle Outreach",
    body: "Our professional call center team runs targeted campaigns to connect with motivated landlords, property owners, and qualified prospects on your behalf.",
  },
  {
    num: "03",
    title: "You Get Qualified Leads",
    body: "Receive warm, pre-vetted leads directly to your pipeline. Real people, real interest — ready for your expert follow-up and conversion.",
  },
];

const activity = [
  { text: "Outbound call to Sandton landlord", active: true },
  { text: "Lead qualified — 3-bed property", active: true },
  { text: "Agent match request sent", active: false },
  { text: "Viewing scheduled for Friday", active: false },
];

export default function ProcessSection() {
  return (
    <Box
      py={{ base: 20, md: 32 }}
      px={{ base: 6, md: 12, lg: 20 }}
      bg="brand.charcoal"
    >
      <Box maxW="1400px" mx="auto">
        <MotionBox
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          mb={20}
        >
          <HStack mb={4} spacing={3}>
            <Box w="32px" h="1px" bg="brand.coral" />
            <Text
              fontSize="xs"
              letterSpacing="0.3em"
              textTransform="uppercase"
              color="brand.coral"
              fontWeight="600"
            >
              How It Works
            </Text>
          </HStack>
          <Heading
            fontFamily="'Playfair Display', serif"
            fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
            fontWeight="400"
            color="brand.cream"
            lineHeight="1.2"
          >
            Three steps to
            <Text as="span" fontStyle="italic" color="brand.coral">
              {" "}
              more listings.
            </Text>
          </Heading>
        </MotionBox>

        <Grid
          templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
          gap={{ base: 12, lg: 0 }}
          alignItems="center"
        >
          {/* Steps */}
          <Box>
            {steps.map((step, i) => (
              <MotionBox
                key={i}
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.2 }}
                display="flex"
                gap={8}
                borderBottom={
                  i < 2 ? "1px solid rgba(46,46,46,0.6)" : "none"
                }
                py={10}
              >
                <Box flexShrink={0}>
                  <Text
                    fontFamily="'Playfair Display', serif"
                    fontSize={{ base: "3xl", md: "4xl" }}
                    color="brand.coral"
                    opacity={0.4}
                    lineHeight="1"
                    fontStyle="italic"
                  >
                    {step.num}
                  </Text>
                </Box>
                <Box>
                  <Text
                    fontFamily="'Playfair Display', serif"
                    fontSize={{ base: "lg", md: "xl" }}
                    color="brand.cream"
                    mb={3}
                  >
                    {step.title}
                  </Text>
                  <Text
                    color="rgba(245,240,235,0.55)"
                    fontSize="sm"
                    lineHeight="1.9"
                  >
                    {step.body}
                  </Text>
                </Box>
              </MotionBox>
            ))}
          </Box>

          {/* Visual panel */}
          <MotionBox
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            pl={{ base: 0, lg: 20 }}
            display="flex"
            alignItems="center"
            justifyContent={{ base: "flex-start", lg: "center" }}
          >
            <Box position="relative" w="full" maxW="400px">
              <Box
                bg="brand.graphite"
                border="1px solid rgba(46,46,46,0.8)"
                p={8}
              >
                <Text
                  fontSize="xs"
                  letterSpacing="0.2em"
                  color="brand.coral"
                  textTransform="uppercase"
                  mb={6}
                  fontWeight="600"
                >
                  Live Activity
                </Text>
                {activity.map((item, j) => (
                  <MotionBox
                    key={j}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: j * 0.15 + 0.4 }}
                  >
                    <HStack mb={j < 3 ? 4 : 0} spacing={4}>
                      <Box
                        w="6px"
                        h="6px"
                        borderRadius="50%"
                        bg={item.active ? "brand.coral" : "rgba(224,92,58,0.2)"}
                        flexShrink={0}
                      />
                      <Text
                        fontSize="xs"
                        color={
                          item.active
                            ? "brand.cream"
                            : "rgba(245,240,235,0.3)"
                        }
                        lineHeight="1.5"
                      >
                        {item.text}
                      </Text>
                      {j === 0 && (
                        <motion.div
                          animate={{
                            scale: [1, 1.5, 1],
                            opacity: [1, 0.5, 1],
                          }}
                          transition={{
                            duration: 1.5,
                            repeat: Infinity,
                          }}
                          style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            background: "#E05C3A",
                            marginLeft: "auto",
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </HStack>
                  </MotionBox>
                ))}
              </Box>
              {/* Corner accents */}
              <Box
                position="absolute"
                top="-10px"
                right="-10px"
                w="36px"
                h="36px"
                borderTop="1px solid"
                borderRight="1px solid"
                borderColor="rgba(224,92,58,0.4)"
              />
              <Box
                position="absolute"
                bottom="-10px"
                left="-10px"
                w="36px"
                h="36px"
                borderBottom="1px solid"
                borderLeft="1px solid"
                borderColor="rgba(224,92,58,0.4)"
              />
            </Box>
          </MotionBox>
        </Grid>
      </Box>
    </Box>
  );
}
